import {useSortable} from '@dnd-kit/sortable'
import {testIdProps} from '@github-ui/test-id-props'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {clsx} from 'clsx'
import {type CSSProperties, type FC, type HTMLAttributes, useMemo} from 'react'

import {DragAndDropItemContext} from '../context/DragAndDropItemContext'
import {useDragAndDrop} from '../hooks/use-drag-and-drop'
import type {DragAndDropItemBase} from '../utils/types'
import styles from './DragAndDropItem.module.css'
import {DragAndDropTrigger} from './DragAndDropTrigger'

export interface DragAndDropItemProps extends Omit<HTMLAttributes<HTMLLIElement>, 'id' | 'title'> {
  /**
   * The index of the list item
   */
  index: number
  /**
   * Sx to apply to the container
   */
  containerStyle?: CSSProperties
  /**
   * Whether or not to hide the trigger, only use when defining a custom trigger.
   */
  hideTrigger?: boolean
  /**
   * The role of the list item
   */
  role?: React.AriaRole
  /**
   * Component type to render for drag and drop container
   */
  as?: React.ElementType
  /**
   * Whether or not the item is a drag overlay
   */
  isDragOverlay?: boolean
  /**
   * Styling for the item nested inside the `li` (default) element
   */
  itemClassName?: string
  /**
   * HTML id for the list item
   */
  elementId?: string
}

/**
 *
 * A sortable list item. This component is responsible for managing the drag trigger and preview.
 *
 * Note: enabling `hideTrigger` and defining a custom trigger is very risky please reference the accessibility
 * grabberIcon [docs](https://github.com/github/accessibility/blob/aa85cdd00de58509aee9eaba18718d2bd727e777/docs/coaching-recommendations/drag-and-drop/technical-guidance/grabber-iconButton.md)
 * to ensure your trigger will be accessible.
 *
 * @param props DragAndDropItemProps
 */
export const DragAndDropItem: FC<DragAndDropItemProps & DragAndDropItemBase> = ({
  children,
  id,
  index,
  title,
  containerStyle,
  style,
  hideTrigger,
  isDragOverlay = false,
  role,
  as = 'li',
  itemClassName,
  elementId,
  ...props
}) => {
  const prefersReducedMotion = usePrefersReducedMotion()
  const {setNodeRef, isDragging, transform, transition} = useSortable({
    id,
    data: {metadata: {id}},
  })

  const {direction, isInDragMode} = useDragAndDrop()

  const containerClassName = clsx(
    'position-relative',
    props.className,
    isInDragMode && {
      [styles.dragItem]: isDragging,
      [styles.horizontal]: direction === 'horizontal',
      [styles.vertical]: direction === 'vertical',
    },
  )

  const itemStyle: React.CSSProperties = {
    transform: transform
      ? `
        translateX(${Math.round(transform.x)}px)
        translateY(${Math.round(transform.y)}px)
        scaleX(${transform.scaleX})
        scaleY(${transform.scaleY})
      `
      : 'none',
    transition: prefersReducedMotion ? 'none' : transition,
  }

  const As = isDragOverlay ? 'div' : as

  return (
    <DragAndDropItemContext.Provider value={useMemo(() => ({index, title, id}), [index, id, title])}>
      <As
        {...props}
        {...testIdProps(isDragOverlay ? 'drag-overlay' : 'sortable-item')}
        className={containerClassName}
        style={{...(isDragOverlay ? {} : containerStyle), ...itemStyle}}
        ref={setNodeRef}
        id={elementId}
      >
        <div
          style={style}
          className={clsx(
            'position-relative',
            itemClassName,
            isInDragMode && styles.contents,
            isDragOverlay && styles.dragOverlay,
          )}
          role={role}
        >
          {!hideTrigger && <DragAndDropTrigger />}
          {children}
        </div>
      </As>
    </DragAndDropItemContext.Provider>
  )
}

try{ DragAndDropItem.displayName ||= 'DragAndDropItem' } catch {}